import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { fmtDuration } from "../lib/duration";
import { toast } from "sonner";
import {
    Boat,
    ClipboardText,
    PauseCircle,
    Warning,
    ChatCircleText,
    Flag,
    CheckCircle,
    Clock,
} from "@phosphor-icons/react";

const FILTERS = [
    { id: "all", label: "All" },
    { id: "report", label: "Reports", kinds: ["report"] },
    { id: "delay", label: "Delays", kinds: ["delay"] },
    { id: "remark", label: "Remarks", kinds: ["remark"] },
];

function fmtTime(iso) {
    if (!iso) return "—";
    return new Date(iso).toLocaleString(undefined, {
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
    });
}

function durStr(min) {
    return fmtDuration(min);
}

function severityColor(s) {
    if (s === "major") return { bg: "#FEE2E2", border: "var(--grindrod-red)", text: "#7F1D1D" };
    if (s === "minor") return { bg: "#F1F5F9", border: "#64748B", text: "#1E293B" };
    return { bg: "#E0E7FF", border: "var(--grindrod-navy)", text: "#1E1B4B" };
}

function eventIcon(e) {
    if (e.kind === "report") return ClipboardText;
    if (e.kind === "delay") return e.source === "pause" ? PauseCircle : Warning;
    if (e.kind === "remark") return ChatCircleText;
    if (e.kind === "vessel_start") return Boat;
    if (e.kind === "vessel_complete") return CheckCircle;
    return Flag;
}

export default function UnifiedTimeline({ vesselId, printMode = false }) {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("all");
    const [now, setNow] = useState(Date.now());

    const load = async () => {
        try {
            const { data } = await api.get(`/vessels/${vesselId}/timeline`);
            setEvents(Array.isArray(data) ? data : []);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { if (vesselId) load(); /* eslint-disable-next-line */ }, [vesselId]);
    useEffect(() => {
        const t = setInterval(() => setNow(Date.now()), 60_000);
        return () => clearInterval(t);
    }, []);

    const filtered = useMemo(() => {
        if (filter === "all") return events;
        const f = FILTERS.find((x) => x.id === filter);
        return events.filter((e) => f?.kinds?.includes(e.kind));
    }, [events, filter]);

    const summary = useMemo(() => {
        let major = 0, standard = 0, minor = 0;
        let majMin = 0, stdMin = 0, minMin = 0;
        for (const e of events) {
            if (e.kind !== "delay") continue;
            const m = e.duration_minutes || 0;
            if (e.severity === "major") { major++; majMin += m; }
            else if (e.severity === "minor") { minor++; minMin += m; }
            else { standard++; stdMin += m; }
        }
        return { major, standard, minor, majMin, stdMin, minMin };
    }, [events]);

    if (loading) return <div className="text-sm text-slate-400 p-6">Loading timeline…</div>;
    if (events.length === 0) return null;

    return (
        <div className={`g-card overflow-hidden ${printMode ? "" : "shadow-sm"}`} data-testid="unified-timeline">
            <div className="g-card-header-dark">
                <Clock size={18} />
                <div className="font-display font-bold uppercase tracking-wide flex-1">Operational Timeline</div>
                <div className="flex items-center gap-3 text-[10px]">
                    <SevPill color="#fee2e2" border="var(--grindrod-red)" text="#7f1d1d" label={`MAJOR ${summary.major} · ${durStr(summary.majMin)}`} />
                    <SevPill color="#e0e7ff" border="var(--grindrod-navy)" text="#1e1b4b" label={`STD ${summary.standard} · ${durStr(summary.stdMin)}`} />
                    <SevPill color="#f1f5f9" border="#64748b" text="#1e293b" label={`MIN ${summary.minor} · ${durStr(summary.minMin)}`} />
                </div>
            </div>

            {!printMode && (
                <div className="flex flex-wrap gap-1.5 px-4 py-2 border-b border-slate-100" data-testid="timeline-filters">
                    {FILTERS.map((f) => (
                        <button
                            key={f.id}
                            onClick={() => setFilter(f.id)}
                            className={`text-[11px] uppercase tracking-wider font-semibold px-3 py-1.5 border transition-colors ${
                                filter === f.id
                                    ? "bg-[var(--grindrod-navy)] text-white border-[var(--grindrod-navy)]"
                                    : "bg-white text-slate-500 border-slate-200 hover:border-[var(--grindrod-navy)]"
                            }`}
                            data-testid={`timeline-filter-${f.id}`}
                        >
                            {f.label}
                        </button>
                    ))}
                </div>
            )}

            <div className="relative">
                {/* Vertical rail */}
                <div className="absolute left-[28px] top-0 bottom-0 w-0.5 bg-slate-200 pointer-events-none" />
                <ol className="space-y-0 py-2" data-testid="timeline-events">
                    {filtered.map((e, i) => {
                        const Icon = eventIcon(e);
                        const isDelay = e.kind === "delay";
                        const sev = severityColor(e.severity);
                        const isPast = e.at && new Date(e.at).getTime() < now;
                        return (
                            <li
                                key={i}
                                className="relative pl-16 pr-4 py-3 hover:bg-slate-50 transition-colors"
                                style={isDelay ? { borderLeft: `4px solid ${sev.border}`, marginLeft: 0 } : {}}
                                data-testid={`timeline-event-${i}`}
                            >
                                <div
                                    className="absolute left-[16px] top-3.5 w-6 h-6 rounded-full flex items-center justify-center shadow"
                                    style={{
                                        background: e.kind === "vessel_complete" ? "var(--grindrod-green)" :
                                                    e.kind === "report" ? "var(--grindrod-navy)" :
                                                    isDelay && e.severity === "major" ? "var(--grindrod-red)" :
                                                    isDelay ? "#475569" :
                                                    e.kind === "remark" ? "#0F2553" :
                                                    "var(--grindrod-navy)",
                                        color: "white",
                                    }}
                                >
                                    <Icon size={12} weight="bold" />
                                </div>
                                <div className="flex items-start gap-3 flex-wrap">
                                    <div className="text-[11px] uppercase-label tabular text-slate-500 w-32 shrink-0 pt-0.5">
                                        {fmtTime(e.at)}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <div className="font-display font-bold text-sm text-[var(--grindrod-navy)]">{e.title}</div>
                                            {isDelay && e.severity && (
                                                <span
                                                    className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm"
                                                    style={{ background: sev.bg, color: sev.text, border: `1px solid ${sev.border}` }}
                                                >
                                                    {e.severity === "major" ? "MAJOR DELAY" : e.severity}
                                                </span>
                                            )}
                                            {isDelay && e.duration_minutes != null && (
                                                <span className="text-[10px] text-slate-500 tabular font-semibold">
                                                    {durStr(e.duration_minutes)}
                                                </span>
                                            )}
                                            {isDelay && e.open && (
                                                <span className="text-[10px] uppercase font-bold text-[var(--grindrod-red)] animate-pulse">
                                                    ongoing
                                                </span>
                                            )}
                                        </div>
                                        {e.detail && <div className="text-xs text-slate-600 mt-0.5 break-words">{e.detail}</div>}
                                        {e.reported_by && (
                                            <div className="text-[10px] text-slate-400 mt-0.5">— {e.reported_by}</div>
                                        )}
                                    </div>
                                </div>
                                {isPast && i === filtered.length - 1 && !printMode && (
                                    <div className="absolute left-0 right-0 -bottom-px h-px bg-[var(--grindrod-red)]/30" />
                                )}
                            </li>
                        );
                    })}
                </ol>
            </div>
        </div>
    );
}

function SevPill({ color, border, text, label }) {
    return (
        <span
            className="px-1.5 py-0.5 rounded-sm font-bold uppercase tracking-wider"
            style={{ background: color, color: text, border: `1px solid ${border}`, fontSize: 9 }}
        >
            {label}
        </span>
    );
}
